<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="brief-modal-overlay"
      :class="{ active: visible }"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="'brief-modal-title'"
      @click.self="close"
      @keydown.esc="close"
    >
      <div class="brief-modal" ref="modalEl">
        <div class="brief-modal-header">
          <div>
            <h2 id="brief-modal-title">{{ briefData.titel }}</h2>
            <div class="modal-sub">{{ briefData.sub }}</div>
          </div>
          <button class="brief-modal-close" type="button" @click="close" :aria-label="t('briefModal.close')">&times;</button>
        </div>

        <div v-if="briefData.kanaal_hint" class="brief-modal-hint">
          <span class="hint-icon" aria-hidden="true">&#8505;</span>
          <span class="hint-text">{{ briefData.kanaal_hint }}</span>
        </div>

        <div class="brief-modal-body">
          <div class="brief-modal-letter" v-html="renderedHTML"></div>
          <div class="brief-modal-info">
            <h3>{{ briefData.variant === 'bericht' ? t('briefModal.howToUseMessage') : t('briefModal.howToUseBrief') }}</h3>
            <template v-for="(blk, i) in briefData.info" :key="i">
              <p v-if="blk.type === 'intro'" class="info-intro">{{ blk.text }}</p>
              <div v-else class="info-block" :class="blk.type === 'fill' ? 'fill' : 'computed'">
                <span class="info-label">{{ blk.label }}</span>
                <div class="info-title">{{ blk.title }}</div>
                <div class="info-text">{{ blk.text }}</div>
              </div>
            </template>
          </div>
        </div>

        <div class="brief-modal-footer">
          <button class="modal-btn" type="button" @click="copyBrief">
            {{ showCopyStatus ? t('briefModal.copied') : t('briefModal.copy') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useBriefRenderer } from '@/composables/useBriefRenderer'
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()

const props = defineProps({
  open: { type: Boolean, default: false },
  briefData: { type: Object, default: null }
})

const emit = defineEmits(['close'])

const { renderBriefHTML, plainBriefTekst } = useBriefRenderer()

const visible = ref(false)
const showCopyStatus = ref(false)
const modalEl = ref(null)

const renderedHTML = computed(() => {
  if (!props.briefData) return ''
  return renderBriefHTML(props.briefData.tekst)
})

watch(() => props.open, (val) => {
  if (val) {
    document.body.classList.add('modal-open')
    nextTick(() => {
      visible.value = true
      if (modalEl.value) modalEl.value.focus()
    })
  } else {
    visible.value = false
    document.body.classList.remove('modal-open')
  }
})

onUnmounted(() => {
  document.body.classList.remove('modal-open')
})

function close() {
  emit('close')
}

function copyBrief() {
  if (!props.briefData) return
  const plain = plainBriefTekst(props.briefData.tekst)

  const onSuccess = () => {
    showCopyStatus.value = true
    setTimeout(() => { showCopyStatus.value = false }, 2500)
    fetch('/api/counters/kopieen', { method: 'POST' }).catch(() => {})
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(plain).then(onSuccess).catch(() => {
      fallbackCopy(plain, onSuccess)
    })
  } else {
    fallbackCopy(plain, onSuccess)
  }
}

function fallbackCopy(text, onSuccess) {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.style.position = 'fixed'
  ta.style.left = '-9999px'
  document.body.appendChild(ta)
  ta.select()
  try {
    document.execCommand('copy')
    onSuccess()
  } catch (e) { /* fail silently */ }
  document.body.removeChild(ta)
}

</script>

<style scoped>
.brief-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 31, 56, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  transition: opacity 0.25s ease;
}
.brief-modal-overlay.active {
  opacity: 1;
}
.brief-modal {
  background: var(--bg-card);
  border-radius: 24px;
  width: 100%;
  max-width: 1100px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(14, 42, 78, 0.3);
  transform: translateY(20px) scale(0.98);
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.brief-modal-overlay.active .brief-modal {
  transform: translateY(0) scale(1);
}
.brief-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid var(--line-soft);
  background: var(--bg);
  flex-shrink: 0;
}
.brief-modal-header h2 {
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.025em;
  color: var(--ink);
  margin: 0;
}
.brief-modal-header .modal-sub {
  font-size: 13px;
  color: var(--ink-soft);
  margin-top: 4px;
}
.brief-modal-close {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid var(--line-soft);
  background: var(--bg-card);
  color: var(--ink);
  font-size: 24px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, border-color 0.2s;
}
.brief-modal-close:hover { background: var(--bg-warm); border-color: var(--ink); }

.brief-modal-hint {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 32px;
  background: #FFF8E1;
  border-bottom: 1px solid var(--line-soft);
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink);
  flex-shrink: 0;
}
.brief-modal-hint .hint-icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--yellow);
  color: var(--ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
  margin-top: 1px;
}
.brief-modal-hint .hint-text { flex: 1; }

.brief-modal-body {
  display: grid;
  grid-template-columns: 2.2fr 1fr;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}
.brief-modal-letter {
  padding: 40px 48px;
  overflow-y: auto;
  background: var(--bg-card);
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  line-height: 1.75;
  color: var(--ink);
  white-space: pre-wrap;
  border-right: 1px solid var(--line-soft);
}
.brief-modal-letter :deep(.placeholder) {
  background: var(--yellow);
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
  cursor: help;
  transition: background 0.15s;
}
.brief-modal-letter :deep(.placeholder:hover),
.brief-modal-letter :deep(.placeholder.active) {
  background: var(--yellow-soft);
  outline: 2px solid var(--yellow);
  outline-offset: 1px;
}
.brief-modal-letter :deep(.computed) {
  background: var(--blue-pale);
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
  cursor: help;
  transition: background 0.15s;
}
.brief-modal-letter :deep(.computed:hover),
.brief-modal-letter :deep(.computed.active) {
  background: var(--blue-light);
  outline: 2px solid var(--blue-light);
  outline-offset: 1px;
}

.brief-modal-info {
  padding: 28px 24px;
  overflow-y: auto;
  background: var(--bg);
  opacity: 0.8;
  transition: opacity 0.2s;
}
.brief-modal-info:hover {
  opacity: 1;
}
.brief-modal-info h3 {
  font-family: 'Fraunces', serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin-bottom: 4px;
  color: var(--ink-soft);
}
.brief-modal-info .info-intro {
  font-size: 12px;
  color: var(--ink-muted);
  line-height: 1.55;
  margin-bottom: 20px;
}
.info-block {
  padding: 12px 0;
  border-top: 1px solid var(--line-soft);
}
.info-block:last-child { border-bottom: 1px solid var(--line-soft); }
.info-block .info-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 6px;
}
.info-block.fill .info-label {
  background: var(--yellow);
  color: var(--ink);
}
.info-block.computed .info-label {
  background: var(--blue-pale);
  color: var(--blue-dark);
}
.info-block .info-title {
  font-family: 'Fraunces', serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
  color: var(--ink-soft);
}
.info-block .info-text {
  font-size: 12px;
  color: var(--ink-muted);
  line-height: 1.55;
}

.brief-modal-footer {
  padding: 16px 32px 20px;
  border-top: 1px solid var(--line-soft);
  background: var(--bg);
  flex-shrink: 0;
}
.brief-modal-footer .modal-btn {
  width: 100%;
  background: var(--blue);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 16px 22px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.brief-modal-footer .modal-btn:hover {
  background: var(--blue-dark);
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .brief-modal-overlay { padding: 0; }
  .brief-modal {
    border-radius: 0;
    max-width: 100%;
    max-height: 100vh;
    height: 100vh;
  }
  .brief-modal-header {
    padding: 16px 20px;
  }
  .brief-modal-header h2 { font-size: 18px; }
  .brief-modal-hint {
    padding: 12px 20px;
    font-size: 13px;
  }
  .brief-modal-body {
    grid-template-columns: 1fr;
  }
  .brief-modal-info { display: none; }
  .brief-modal-letter {
    padding: 24px 20px;
    border-right: none;
    font-size: 14px;
  }
  .brief-modal-footer {
    padding: 12px 20px 20px;
  }
}
</style>
